import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  de: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'Über uns',
      services: 'Leistungen',
      pricing: 'Preise',
      faq: 'FAQ',
      gallery: 'Galerie',
      contact: 'Kontakt',
      bookNow: 'Jetzt buchen',
    },
    // Hero
    hero: {
      badge: 'Flughafen Düsseldorf',
      title1: 'Premium',
      title2: 'Valet Parking',
      title3: 'am Flughafen',
      description: 'Stressfrei parken mit unserem exklusiven Valet- und Shuttle-Service. Nur 1 Minute vom Terminal entfernt. Sicher, bequem und zuverlässig.',
      cta: 'Jetzt buchen',
      services: 'Unsere Leistungen',
    },
    // Benefits
    benefits: {
      terminal: 'Nur 1 Minute zum Terminal',
      secure: 'Gesichertes Gelände mit Schranken- & Toranlagen',
      service: 'Service von 5-23 Uhr',
      cancel: 'Kostenlose Stornierung',
    },
    // Stats
    stats: {
      experience: 'Jahre Erfahrung',
      parkingSpots: 'Parkplätze',
      satisfaction: 'Kundenzufriedenheit',
      parkedCars: 'Geparkte Autos',
    },
    // Services
    services: {
      title: 'Unsere Services',
      subtitle: 'Alles aus einer Hand',
      description: 'Wählen Sie den Service, der am besten zu Ihren Bedürfnissen passt.',
      valet: {
        title: 'Valet Parking',
        description: 'Übergeben Sie Ihr Fahrzeug bequem am Terminal. Wir parken es sicher für Sie.',
      },
      shuttle: {
        title: 'Shuttle Service',
        description: 'Kostenloser Shuttle-Transfer zwischen Parkplatz und Flughafen-Terminal.',
      },
      carcare: {
        title: 'Autopflege',
        description: 'Professionelle Fahrzeugpflege während Ihrer Abwesenheit auf Wunsch.',
      },
      learnMore: 'Mehr erfahren',
    },
    // Why Us
    whyUs: {
      subtitle: 'Warum ParkingDUS?',
      title1: 'Ihr Vertrauen ist unser',
      title2: 'Antrieb',
      description: 'Mit über 10 Jahren Erfahrung am Flughafen Düsseldorf bieten wir Ihnen erstklassigen Parkservice. Unser Team steht Ihnen von 5-23 Uhr zur Verfügung, um Ihre Reise so angenehm wie möglich zu gestalten.',
      features: [
        'Direkt am Flughafen – nur 1 Minute zum Terminal',
        'Service von 5-23 Uhr mit persönlichem Ansprechpartner',
        'Gesichertes Gelände mit Schranken- & Toranlagen',
        'Eigene Waschanlage & Pflegehallen',
        'Bis 24 Stunden vorher kostenlos stornierbar',
      ],
      cta: 'Parkplatz reservieren',
      rating: 'Kundenbewertung',
    },
    // CTA
    cta: {
      title1: 'Bereit für eine',
      title2: 'stressfreie',
      title3: 'Reise?',
      description: 'Buchen Sie jetzt Ihren Parkplatz und genießen Sie unseren Premium-Service am Flughafen Düsseldorf.',
      button: 'Jetzt Parkplatz buchen',
    },
    // Footer
    footer: {
      description: 'Ihr Premium-Parkservice am Flughafen Düsseldorf. Stressfrei parken mit Valet- und Shuttle-Service.',
      service24: 'Service 5-23 Uhr',
      navigation: 'Navigation',
      contact: 'Kontakt',
      legal: 'Rechtliches',
      impressum: 'Impressum',
      privacy: 'Datenschutz',
      copyright: 'ParkingDUS – MCD Group GmbH. Alle Rechte vorbehalten.',
      madeWith: 'Made with',
      inDusseldorf: 'in Düsseldorf by',
    },
    // Cookie Banner
    cookie: {
      title: 'Cookie-Einstellungen',
      description: 'Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Einige Cookies sind für die Funktionalität der Website erforderlich, während andere uns helfen, unsere Website zu verbessern.',
      learnMore: 'Mehr erfahren',
      acceptAll: 'Alle akzeptieren',
      rejectAll: 'Nur notwendige',
      settings: 'Einstellungen',
      necessary: 'Notwendige Cookies',
      necessaryDesc: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich.',
      functional: 'Funktionale Cookies',
      functionalDesc: 'Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung.',
      analytics: 'Analyse Cookies',
      analyticsDesc: 'Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen.',
      marketing: 'Marketing Cookies',
      marketingDesc: 'Diese Cookies werden verwendet, um Werbung relevanter zu gestalten.',
      saveSettings: 'Auswahl speichern',
      back: 'Zurück',
    },
    // About Page
    about: {
      subtitle: 'Über uns',
      title1: 'Ihr Partner für',
      title2: 'Premium-Parken',
      description: 'ParkingDUS ist Ihr zuverlässiger Partner für stressfreies Parken am Flughafen Düsseldorf. Mit über 10 Jahren Erfahrung und einem engagierten Team bieten wir Ihnen erstklassigen Service zu fairen Preisen.',
      contactBtn: 'Kontakt aufnehmen',
      yearsExp: 'Jahre Erfahrung',
      missionSubtitle: 'Unsere Mission',
      missionTitle: 'Stressfreies Parken für jeden Reisenden',
      missionDesc: 'Wir glauben, dass der Urlaub bereits bei der Anfahrt zum Flughafen beginnen sollte. Deshalb haben wir es uns zur Aufgabe gemacht, das Parken am Flughafen so einfach und angenehm wie möglich zu gestalten.',
      values: {
        security: { title: 'Sicherheit', desc: 'Ihr Fahrzeug ist bei uns in besten Händen. Geschultes Personal garantiert maximale Sicherheit.' },
        reliability: { title: 'Zuverlässigkeit', desc: 'Pünktlichkeit ist unsere Stärke. Wir sind für Sie da – täglich von 5-23 Uhr.' },
        service: { title: 'Kundenservice', desc: 'Ihre Zufriedenheit steht im Mittelpunkt. Unser Team kümmert sich persönlich um all Ihre Anliegen.' },
        quality: { title: 'Qualität', desc: 'Wir setzen auf höchste Standards in allen Bereichen – von der Buchung bis zur Fahrzeugübergabe.' },
      },
      historySubtitle: 'Unsere Geschichte',
      historyTitle: 'Der Weg zu Ihrem Vertrauen',
      timeline: [
        { year: '2014', title: 'Gründung', desc: 'Start mit nur 50 Parkplätzen und einer Vision für Premium-Parkservice.' },
        { year: '2016', title: 'Expansion', desc: 'Erweiterung auf 200 Parkplätze und Einführung des Valet-Service.' },
        { year: '2019', title: 'Modernisierung', desc: 'Implementierung des Online-Buchungssystems.' },
        { year: 'Heute', title: 'Führender Anbieter', desc: '500 Parkplätze und einer der führenden Anbieter am Flughafen Düsseldorf.' },
      ],
      teamSubtitle: 'Unser Team',
      teamTitle1: 'Menschen, die sich',
      teamTitle2: 'kümmern',
      teamDesc: 'Hinter ParkingDUS steht ein engagiertes Team von Profis, die eines gemeinsam haben: die Leidenschaft für exzellenten Service. Jedes Teammitglied ist geschult und motiviert, Ihnen das beste Parkerlebnis zu bieten.',
      teamFeatures: [
        'Geschultes und freundliches Personal',
        'Persönlicher Ansprechpartner bei Fragen',
        'Erreichbarkeit von 5-23 Uhr für Ihre Anliegen',
        'Regelmäßige Schulungen für höchste Qualität',
      ],
      ctaTitle: 'Überzeugen Sie sich selbst',
      ctaDesc: 'Erleben Sie unseren Premium-Service und starten Sie entspannt in Ihren Urlaub.',
      ctaBtn: 'Jetzt Parkplatz reservieren',
    },
    // Services Page
    servicesPage: {
      subtitle: 'Unsere Leistungen',
      title1: 'Services, die',
      title2: 'begeistern',
      description: 'Wählen Sie den Service, der am besten zu Ihren Bedürfnissen passt. Von Valet-Parking bis Autopflege – wir haben an alles gedacht.',
      valet: {
        subtitle: 'Der komfortabelste Weg',
        title: 'Valet Parking',
        description: 'Übergeben Sie Ihr Fahrzeug direkt am Terminal an unser geschultes Personal. Bei Ihrer Rückkehr steht Ihr Auto bereits für Sie bereit.',
        features: [
          'Fahrzeugübergabe direkt am Abflug-Terminal',
          'Ihr Auto wartet bei Rückkehr auf Sie',
          'Keine Parkplatzsuche, kein Shuttle nötig',
          'Geschultes und versichertes Personal',
          'Ideal für Geschäftsreisende',
        ],
      },
      shuttle: {
        subtitle: 'Die günstige Alternative',
        title: 'Shuttle Service',
        description: 'Parken Sie Ihr Fahrzeug sicher auf unserem Parkplatz und nutzen Sie unseren kostenlosen Shuttle-Service zum Terminal.',
        features: [
          'Kostenloser Shuttle-Transfer zum Terminal',
          'Nur 1 Minute Fahrtzeit zum Flughafen',
          'Flexible Abholzeiten',
          'Kein Warten – individueller Transfer',
          'Günstigste Option für Langzeitparken',
        ],
      },
      carcare: {
        subtitle: 'Zusätzlicher Service',
        title: 'Autopflege',
        description: 'Nutzen Sie Ihre Abwesenheit und lassen Sie Ihr Fahrzeug professionell reinigen. In unserer eigenen Waschanlage und Pflegehallen kommen Sie zurück zu einem strahlenden Auto.',
        features: [
          'Eigene Waschanlage vor Ort',
          'Professionelle Pflegehallen',
          'Komplette Innen- und Außenreinigung',
          'Politur und Lackversiegelung',
          'Verschiedene Pakete verfügbar',
        ],
      },
      viewPrices: 'Preise ansehen',
      processSubtitle: 'So funktioniert\'s',
      processTitle: 'In 4 Schritten zum Parkplatz',
      process: [
        { step: '01', title: 'Online buchen', desc: 'Reservieren Sie Ihren Parkplatz bequem online in wenigen Minuten.' },
        { step: '02', title: 'Anreise', desc: 'Fahren Sie direkt zu unserem Standort oder zum Terminal (Valet).' },
        { step: '03', title: 'Entspannt reisen', desc: 'Wir kümmern uns um alles – genießen Sie Ihre Reise.' },
        { step: '04', title: 'Rückkehr', desc: 'Ihr Fahrzeug wartet bereits auf Sie – sauber und sicher.' },
      ],
      benefitsSubtitle: 'Ihre Vorteile',
      benefitsTitle: 'Warum ParkingDUS?',
      benefitsList: [
        { title: 'Beste Lage', desc: 'Nur 1 Minute vom Terminal entfernt – näher geht es nicht.' },
        { title: 'Gesichertes Gelände', desc: 'Schranken- & Toranlagen für maximale Sicherheit.' },
        { title: 'Service 5-23 Uhr', desc: 'Täglich erreichbar für alle Ihre Fragen.' },
        { title: 'Kostenlose Stornierung', desc: 'Bis 24 Stunden vorher ohne Gebühren.' },
        { title: 'Eigene Waschanlage', desc: 'Professionelle Fahrzeugpflege in unseren Pflegehallen.' },
        { title: 'Zusatzservices', desc: 'Autopflege und mehr auf Wunsch verfügbar.' },
      ],
      ctaTitle: 'Bereit zum Buchen?',
      ctaDesc: 'Sichern Sie sich jetzt Ihren Parkplatz und starten Sie entspannt in den Urlaub.',
      ctaBtn: 'Jetzt Parkplatz buchen',
      callBtn: 'Anrufen',
    },
    // Pricing Page
    pricing: {
      subtitle: 'Unsere Preise',
      title1: 'Transparent &',
      title2: 'fair',
      description: 'Keine versteckten Kosten, keine Überraschungen. Wählen Sie den Service, der zu Ihnen passt, und erhalten Sie sofort Ihren Preis.',
      shuttle: {
        name: 'Shuttle Parking',
        desc: 'Die günstige Option mit kostenlosem Transfer',
        price: 'ab 72',
        unit: '€',
        weekPrice: 'Pauschalpreis',
        features: [
          'Kostenloser Shuttle-Transfer',
          'Nur 1 Minute zum Terminal',
          'Gesichertes Gelände mit Schranken- & Toranlagen',
          'Service 5-23 Uhr',
          'Kostenlose Stornierung',
        ],
        cta: 'Shuttle buchen',
      },
      valet: {
        name: 'Valet Parking',
        desc: 'Maximum an Komfort – direkt am Terminal',
        price: 'ab 72',
        unit: '€',
        weekPrice: 'Pauschalpreis',
        features: [
          'Fahrzeugübergabe am Terminal',
          'Auto wartet bei Rückkehr',
          'Keine Parkplatzsuche nötig',
          'Geschultes Personal',
          'Premium-Service',
          'Inkl. kostenlose Stornierung',
        ],
        cta: 'Valet buchen',
        popular: 'BELIEBT',
      },
      carcare: {
        name: 'Autopflege',
        desc: 'Zusatzservice für strahlende Rückkehr',
        price: 'ab 49',
        unit: '€',
        weekPrice: 'Einmalpreis',
        features: [
          'Eigene Waschanlage vor Ort',
          'Professionelle Pflegehallen',
          'Innenraumreinigung',
          'Buchbar mit allen Services',
          'Verschiedene Pakete',
        ],
        cta: 'Autopflege hinzufügen',
      },
      priceNote: '* Preise können je nach Saison und Verfügbarkeit variieren. Der exakte Preis wird im Buchungssystem angezeigt.',
      includedSubtitle: 'Inklusivleistungen',
      includedTitle: 'Bei jedem Tarif inklusive',
      included: [
        { title: 'Versicherungsschutz', desc: 'Vollkasko während des gesamten Aufenthalts' },
        { title: 'Service 5-23 Uhr', desc: 'Täglich für Sie erreichbar' },
        { title: 'Kostenlose Stornierung', desc: 'Bis 24h vorher ohne Gebühren' },
        { title: 'Persönlicher Support', desc: 'Direkter Ansprechpartner' },
      ],
      faqSubtitle: 'Häufige Fragen',
      faqTitle: 'Fragen zu unseren Preisen?',
      faqDesc: 'Hier finden Sie Antworten auf die häufigsten Fragen rund um unsere Preise und Zahlungsoptionen.',
      viewAllFaq: 'Alle FAQs ansehen',
      faqs: [
        { q: 'Was kostet das Parken für 2 Wochen?', a: 'Der Preis für 2 Wochen Shuttle-Parking beträgt 72€, Valet-Parking ebenfalls 72€. Den genauen Preis erhalten Sie im Buchungssystem.' },
        { q: 'Sind die Preise inklusive aller Gebühren?', a: 'Ja, alle angegebenen Preise sind Endpreise inklusive Mehrwertsteuer und aller Gebühren.' },
        { q: 'Wann muss ich bezahlen?', a: 'Die Bezahlung erfolgt bei Abholung des Fahrzeugs. Keine Vorkasse erforderlich.' },
        { q: 'Kann ich kostenfrei stornieren?', a: 'Ja, bis 24 Stunden vor Anreise können Sie kostenfrei stornieren.' },
      ],
      ctaTitle: 'Jetzt Preis berechnen',
      ctaDesc: 'Geben Sie Ihre Reisedaten ein und erhalten Sie sofort den genauen Preis für Ihren Parkplatz.',
      ctaBtn: 'Zum Buchungssystem',
    },
    // FAQ Page
    faq: {
      subtitle: 'FAQ',
      title1: 'Häufig gestellte',
      title2: 'Fragen',
      description: 'Hier finden Sie Antworten auf die häufigsten Fragen rund um unseren Parkservice am Flughafen Düsseldorf.',
      categories: [
        {
          title: 'Buchung & Reservierung',
          questions: [
            { q: 'Wie buche ich einen Parkplatz?', a: 'Die Buchung erfolgt ganz einfach über unser Online-Buchungssystem. Wählen Sie Ihre Reisedaten, den gewünschten Service (Shuttle oder Valet) und schließen Sie die Reservierung ab. Sie erhalten sofort eine Buchungsbestätigung per E-Mail.' },
            { q: 'Kann ich meine Buchung ändern oder stornieren?', a: 'Ja, Sie können Ihre Buchung bis 24 Stunden vor Anreise kostenlos stornieren oder ändern. Kontaktieren Sie uns einfach per Telefon oder E-Mail.' },
            { q: 'Muss ich im Voraus bezahlen?', a: 'Nein, bei uns ist keine Vorkasse erforderlich. Sie bezahlen bequem bei Abholung Ihres Fahrzeugs.' },
            { q: 'Erhalte ich eine Buchungsbestätigung?', a: 'Ja, direkt nach Ihrer Buchung erhalten Sie eine Bestätigung per E-Mail mit allen wichtigen Informationen zu Ihrem Parkplatz.' },
          ],
        },
        {
          title: 'Anreise & Ablauf',
          questions: [
            { q: 'Wo befindet sich der Parkplatz?', a: 'Unser Parkplatz befindet sich nur 1 Minute vom Flughafen Düsseldorf entfernt. Die genaue Adresse und Anfahrtsbeschreibung erhalten Sie mit Ihrer Buchungsbestätigung.' },
            { q: 'Wie funktioniert der Shuttle-Service?', a: 'Fahren Sie direkt zu unserem Parkplatz, parken Sie Ihr Auto und unser Shuttle bringt Sie innerhalb von 1 Minute zum Terminal. Bei Ihrer Rückkehr rufen Sie uns an, und wir holen Sie direkt am Terminal ab.' },
            { q: 'Wie funktioniert der Valet-Service?', a: 'Beim Valet-Service fahren Sie direkt zum Abflug-Terminal. Dort übernimmt unser geschultes Personal Ihr Fahrzeug und parkt es sicher für Sie. Bei Ihrer Rückkehr steht Ihr Auto bereits am Terminal für Sie bereit.' },
            { q: 'Was passiert, wenn mein Flug Verspätung hat?', a: 'Kein Problem! Wir überwachen die Flugzeiten und passen unseren Service entsprechend an. Sie bezahlen nur für die tatsächliche Parkzeit.' },
          ],
        },
        {
          title: 'Sicherheit & Service',
          questions: [
            { q: 'Ist mein Fahrzeug sicher?', a: 'Ja! Unser Hof ist mit Schranken- und Toranlagen gesichert. Die Schlüsselübergabe erfolgt an unserer Annahmestelle, wo auch eine Kameraüberwachung vorhanden ist. Geschultes Personal betreut den Parkplatz.' },
            { q: 'Wer fährt mein Auto beim Valet-Service?', a: 'Ausschließlich unser geschultes und versichertes Personal. Alle Mitarbeiter werden sorgfältig ausgewählt und regelmäßig geschult.' },
            { q: 'Kann ich meinen Autoschlüssel behalten?', a: 'In der Regel müssen die Schlüssel bei uns abgegeben werden, damit wir die Fahrzeuge bei Bedarf umparken können. Auf Nachfrage ist es jedoch möglich, den Schlüssel zu behalten.' },
            { q: 'Bieten Sie auch Autopflege an?', a: 'Ja, wir haben eine eigene Waschanlage und Pflegehallen vor Ort. Auf Wunsch können Sie verschiedene Autopflege-Pakete dazu buchen – von der einfachen Außenwäsche bis zur kompletten Innen- und Außenreinigung.' },
          ],
        },
        {
          title: 'Preise & Zahlung',
          questions: [
            { q: 'Wie werden die Preise berechnet?', a: 'Die Preise richten sich nach dem gewählten Service (Shuttle oder Valet). Im Buchungssystem sehen Sie sofort den genauen Preis für Ihre Reisedaten.' },
            { q: 'Welche Zahlungsmethoden werden akzeptiert?', a: 'Wir akzeptieren Barzahlung, EC-Karte und alle gängigen Kreditkarten. Die Zahlung erfolgt bei Abholung des Fahrzeugs.' },
            { q: 'Gibt es versteckte Kosten?', a: 'Nein, bei uns gibt es keine versteckten Kosten. Der im Buchungssystem angezeigte Preis ist der Endpreis inklusive Mehrwertsteuer und aller Gebühren.' },
            { q: 'Erhalte ich eine Rechnung?', a: 'Ja, Sie erhalten automatisch eine ordnungsgemäße Rechnung, die Sie auch für Geschäftsreisen nutzen können.' },
          ],
        },
      ],
      contactTitle: 'Noch Fragen?',
      contactDesc: 'Unser Team steht Ihnen gerne zur Verfügung. Kontaktieren Sie uns – wir helfen Ihnen weiter!',
      emailBtn: 'E-Mail schreiben',
      contactFormLink: 'Zum Kontaktformular',
      bookCtaTitle: 'Überzeugt? Dann buchen Sie jetzt!',
      bookCtaDesc: 'Sichern Sie sich Ihren Parkplatz am Flughafen Düsseldorf und starten Sie entspannt in den Urlaub.',
      bookCtaBtn: 'Jetzt Parkplatz buchen',
    },
    // Gallery
    gallery: {
      subtitle: 'Galerie',
      title1: 'Einblicke in unseren',
      title2: 'Service',
      description: 'Überzeugen Sie sich selbst von unserem Premium-Parkservice am Flughafen Düsseldorf.',
      ctaTitle: 'Überzeugt von unserem Service?',
      ctaDesc: 'Buchen Sie jetzt Ihren Parkplatz und erleben Sie unseren Premium-Service selbst.',
      ctaBtn: 'Jetzt Parkplatz buchen',
      categories: {
        parkplatz: 'Parkplatz',
        valet: 'Valet',
        shuttle: 'Shuttle',
        flughafen: 'Flughafen',
        autopflege: 'Autopflege',
        fahrzeuge: 'Fahrzeuge',
        terminal: 'Terminal',
        team: 'Team',
      },
    },
    // Contact
    contact: {
      subtitle: 'Kontakt',
      title1: 'Wir sind für Sie',
      title2: 'da',
      description: 'Haben Sie Fragen oder benötigen Sie Unterstützung? Unser Team hilft Ihnen gerne weiter.',
      phone: 'Telefon',
      phoneAvailable: 'Erreichbar 5-23 Uhr',
      email: 'E-Mail',
      emailResponse: 'Antwort innerhalb 24h',
      address: 'Adresse',
      addressNote: 'Direkt am Flughafen',
      hours: 'Servicezeiten',
      hoursValue: '5-23 Uhr',
      hoursNote: 'Täglich',
      formTitle: 'Schreiben Sie uns',
      formDesc: 'Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.',
      name: 'Name',
      namePlaceholder: 'Ihr Name',
      emailLabel: 'E-Mail',
      emailPlaceholder: 'ihre@email.de',
      phonePlaceholder: '+49 123 456789',
      subject: 'Betreff',
      subjectPlaceholder: 'Bitte wählen',
      subjects: {
        booking: 'Buchungsanfrage',
        price: 'Preisanfrage',
        cancel: 'Stornierung',
        change: 'Buchungsänderung',
        feedback: 'Feedback',
        other: 'Sonstiges',
      },
      message: 'Nachricht',
      messagePlaceholder: 'Ihre Nachricht an uns...',
      submit: 'Nachricht senden',
      successTitle: 'Nachricht gesendet!',
      successDesc: 'Vielen Dank für Ihre Nachricht. Wir werden uns so schnell wie möglich bei Ihnen melden.',
      mapTitle: 'Hier finden Sie uns',
      mapDesc: 'Direkt am Flughafen Düsseldorf – nur 1 Minute zum Terminal.',
      ctaTitle: 'Direkt buchen – ohne Umwege',
      ctaDesc: 'Sichern Sie sich jetzt Ihren Parkplatz am Flughafen Düsseldorf.',
      ctaBtn: 'Zum Buchungssystem',
    },
    // Impressum
    impressum: {
      title: 'Impressum',
      according: 'Angaben gemäß § 5 TMG',
      register: 'Handelsregister',
      court: 'Registergericht',
      representedBy: 'Vertreten durch',
      contactTitle: 'Kontakt',
      vatId: 'Umsatzsteuer-ID',
      vatIdDesc: 'Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:',
      disputeTitle: 'Streitschlichtung',
      disputeText1: 'Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:',
      disputeText2: 'Unsere E-Mail-Adresse finden Sie oben im Impressum.',
      disputeText3: 'Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.',
      liabilityContentTitle: 'Haftung für Inhalte',
      liabilityContentText1: 'Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.',
      liabilityContentText2: 'Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.',
      liabilityLinksTitle: 'Haftung für Links',
      liabilityLinksText1: 'Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben.',
      liabilityLinksText2: 'Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft.',
      copyrightTitle: 'Urheberrecht',
      copyrightText1: 'Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht.',
      copyrightText2: 'Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.',
      toPrivacy: 'Zur Datenschutzerklärung →',
    },
    // Privacy
    privacy: {
      title: 'Datenschutzerklärung',
      lastUpdate: 'Stand: Januar 2025',
      toImpressum: 'Zum Impressum →',
    },
  },
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About Us',
      services: 'Services',
      pricing: 'Pricing',
      faq: 'FAQ',
      gallery: 'Gallery',
      contact: 'Contact',
      bookNow: 'Book Now',
    },
    // Hero
    hero: {
      badge: 'Düsseldorf Airport',
      title1: 'Premium',
      title2: 'Valet Parking',
      title3: 'at the Airport',
      description: 'Stress-free parking with our exclusive valet and shuttle service. Only 1 minute from the terminal. Safe, convenient and reliable.',
      cta: 'Book Now',
      services: 'Our Services',
    },
    // Benefits
    benefits: {
      terminal: 'Only 1 minute to terminal',
      secure: 'Secured premises with barriers & gates',
      service: 'Service 5am-11pm',
      cancel: 'Free cancellation',
    },
    // Stats
    stats: {
      experience: 'Years Experience',
      parkingSpots: 'Parking Spots',
      satisfaction: 'Customer Satisfaction',
      parkedCars: 'Cars Parked',
    },
    // Services
    services: {
      title: 'Our Services',
      subtitle: 'Everything from one source',
      description: 'Choose the service that best suits your needs.',
      valet: {
        title: 'Valet Parking',
        description: 'Hand over your vehicle conveniently at the terminal. We park it safely for you.',
      },
      shuttle: {
        title: 'Shuttle Service',
        description: 'Free shuttle transfer between parking lot and airport terminal.',
      },
      carcare: {
        title: 'Car Care',
        description: 'Professional vehicle care during your absence on request.',
      },
      learnMore: 'Learn more',
    },
    // Why Us
    whyUs: {
      subtitle: 'Why ParkingDUS?',
      title1: 'Your trust is our',
      title2: 'motivation',
      description: 'With over 10 years of experience at Düsseldorf Airport, we offer you first-class parking service. Our team is available daily from 5am-11pm to make your trip as pleasant as possible.',
      features: [
        'Right at the airport – only 1 minute to terminal',
        'Service 5am-11pm with personal contact',
        'Secured premises with barriers & gates',
        'Own car wash & care facilities',
        'Free cancellation up to 24 hours before',
      ],
      cta: 'Reserve parking',
      rating: 'Customer Rating',
    },
    // CTA
    cta: {
      title1: 'Ready for a',
      title2: 'stress-free',
      title3: 'trip?',
      description: 'Book your parking space now and enjoy our premium service at Düsseldorf Airport.',
      button: 'Book parking now',
    },
    // Footer
    footer: {
      description: 'Your premium parking service at Düsseldorf Airport. Stress-free parking with valet and shuttle service.',
      service24: 'Service 5am-11pm',
      navigation: 'Navigation',
      contact: 'Contact',
      legal: 'Legal',
      impressum: 'Imprint',
      privacy: 'Privacy Policy',
      copyright: 'ParkingDUS – MCD Group GmbH. All rights reserved.',
      madeWith: 'Made with',
      inDusseldorf: 'in Düsseldorf by',
    },
    // Cookie Banner
    cookie: {
      title: 'Cookie Settings',
      description: 'We use cookies to provide you with the best possible experience on our website. Some cookies are necessary for the functionality of the website, while others help us improve our website.',
      learnMore: 'Learn more',
      acceptAll: 'Accept all',
      rejectAll: 'Only necessary',
      settings: 'Settings',
      necessary: 'Necessary Cookies',
      necessaryDesc: 'These cookies are required for the basic functions of the website.',
      functional: 'Functional Cookies',
      functionalDesc: 'These cookies enable extended functions and personalization.',
      analytics: 'Analytics Cookies',
      analyticsDesc: 'These cookies help us understand how visitors use our website.',
      marketing: 'Marketing Cookies',
      marketingDesc: 'These cookies are used to make advertising more relevant.',
      saveSettings: 'Save selection',
      back: 'Back',
    },
    // About Page
    about: {
      subtitle: 'About Us',
      title1: 'Your partner for',
      title2: 'premium parking',
      description: 'ParkingDUS is your reliable partner for stress-free parking at Düsseldorf Airport. With over 10 years of experience and a dedicated team, we offer you first-class service at fair prices.',
      contactBtn: 'Get in touch',
      yearsExp: 'Years Experience',
      missionSubtitle: 'Our Mission',
      missionTitle: 'Stress-free parking for every traveler',
      missionDesc: 'We believe that the vacation should start at the drive to the airport. That\'s why we\'ve made it our mission to make airport parking as easy and pleasant as possible.',
      values: {
        security: { title: 'Security', desc: 'Your vehicle is in the best hands with us. Trained personnel guarantee maximum security.' },
        reliability: { title: 'Reliability', desc: 'Punctuality is our strength. We\'re there when you need us – daily from 5am-11pm.' },
        service: { title: 'Customer Service', desc: 'Your satisfaction is our priority. Our team takes care of all your concerns personally.' },
        quality: { title: 'Quality', desc: 'We set the highest standards in all areas – from booking to vehicle handover.' },
      },
      historySubtitle: 'Our History',
      historyTitle: 'The path to your trust',
      timeline: [
        { year: '2014', title: 'Foundation', desc: 'Started with only 50 parking spaces and a vision for premium parking service.' },
        { year: '2016', title: 'Expansion', desc: 'Expansion to 200 parking spaces and introduction of valet service.' },
        { year: '2019', title: 'Modernization', desc: 'Implementation of online booking system.' },
        { year: 'Today', title: 'Leading Provider', desc: '500 parking spaces and one of the leading providers at Düsseldorf Airport.' },
      ],
      teamSubtitle: 'Our Team',
      teamTitle1: 'People who',
      teamTitle2: 'care',
      teamDesc: 'Behind ParkingDUS is a dedicated team of professionals who share one thing: a passion for excellent service. Every team member is trained and motivated to give you the best parking experience.',
      teamFeatures: [
        'Trained and friendly staff',
        'Personal contact for questions',
        'Available 5am-11pm for your concerns',
        'Regular training for highest quality',
      ],
      ctaTitle: 'See for yourself',
      ctaDesc: 'Experience our premium service and start your vacation relaxed.',
      ctaBtn: 'Reserve parking now',
    },
    // Services Page
    servicesPage: {
      subtitle: 'Our Services',
      title1: 'Services that',
      title2: 'inspire',
      description: 'Choose the service that best suits your needs. From valet parking to car care – we\'ve thought of everything.',
      valet: {
        subtitle: 'The most comfortable way',
        title: 'Valet Parking',
        description: 'Hand over your vehicle directly at the terminal to our trained staff. When you return, your car will be waiting for you.',
        features: [
          'Vehicle handover directly at departure terminal',
          'Your car waits for you on return',
          'No parking search, no shuttle needed',
          'Trained and insured personnel',
          'Ideal for business travelers',
        ],
      },
      shuttle: {
        subtitle: 'The affordable alternative',
        title: 'Shuttle Service',
        description: 'Park your vehicle safely at our parking lot and use our free shuttle service to the terminal.',
        features: [
          'Free shuttle transfer to terminal',
          'Only 1 minute drive to airport',
          'Flexible pickup times',
          'No waiting – individual transfer',
          'Most affordable option for long-term parking',
        ],
      },
      carcare: {
        subtitle: 'Additional service',
        title: 'Car Care',
        description: 'Use your absence and have your vehicle professionally cleaned. In our own car wash and care facilities, come back to a shining car.',
        features: [
          'Own car wash on site',
          'Professional care facilities',
          'Complete interior and exterior cleaning',
          'Polish and paint sealing',
          'Various packages available',
        ],
      },
      viewPrices: 'View prices',
      processSubtitle: 'How it works',
      processTitle: 'Your parking in 4 steps',
      process: [
        { step: '01', title: 'Book online', desc: 'Reserve your parking space conveniently online in just a few minutes.' },
        { step: '02', title: 'Arrival', desc: 'Drive directly to our location or to the terminal (Valet).' },
        { step: '03', title: 'Travel relaxed', desc: 'We take care of everything – enjoy your trip.' },
        { step: '04', title: 'Return', desc: 'Your vehicle is already waiting for you – clean and safe.' },
      ],
      benefitsSubtitle: 'Your Benefits',
      benefitsTitle: 'Why ParkingDUS?',
      benefitsList: [
        { title: 'Best Location', desc: 'Only 1 minute from the terminal – you can\'t get closer.' },
        { title: 'Secured Premises', desc: 'Barriers & gate systems for maximum security.' },
        { title: 'Service 5am-11pm', desc: 'Available daily for all your questions.' },
        { title: 'Free Cancellation', desc: 'Up to 24 hours before without fees.' },
        { title: 'Own Car Wash', desc: 'Professional vehicle care in our care facilities.' },
        { title: 'Additional Services', desc: 'Car care and more available on request.' },
      ],
      ctaTitle: 'Ready to book?',
      ctaDesc: 'Secure your parking space now and start your vacation relaxed.',
      ctaBtn: 'Book parking now',
      callBtn: 'Call',
    },
    // Pricing Page
    pricing: {
      subtitle: 'Our Prices',
      title1: 'Transparent &',
      title2: 'fair',
      description: 'No hidden costs, no surprises. Choose the service that suits you and get your price immediately.',
      shuttle: {
        name: 'Shuttle Parking',
        desc: 'The affordable option with free transfer',
        price: 'from 72',
        unit: '€',
        weekPrice: 'Flat rate',
        features: [
          'Free shuttle transfer',
          'Only 1 minute to terminal',
          'Secured premises with barriers & gates',
          'Service 5am-11pm',
          'Free cancellation',
        ],
        cta: 'Book Shuttle',
      },
      valet: {
        name: 'Valet Parking',
        desc: 'Maximum comfort – right at the terminal',
        price: 'from 72',
        unit: '€',
        weekPrice: 'Flat rate',
        features: [
          'Vehicle handover at terminal',
          'Car waits on return',
          'No parking search needed',
          'Trained personnel',
          'Premium service',
          'Incl. free cancellation',
        ],
        cta: 'Book Valet',
        popular: 'POPULAR',
      },
      carcare: {
        name: 'Car Care',
        desc: 'Additional service for a shiny return',
        price: 'from 49',
        unit: '€',
        weekPrice: 'One-time price',
        features: [
          'Own car wash on site',
          'Professional care facilities',
          'Interior cleaning',
          'Bookable with all services',
          'Various packages',
        ],
        cta: 'Add Car Care',
      },
      priceNote: '* Prices may vary depending on season and availability. The exact price is shown in the booking system.',
      includedSubtitle: 'Included Services',
      includedTitle: 'Included in every rate',
      included: [
        { title: 'Insurance Coverage', desc: 'Full coverage during the entire stay' },
        { title: 'Service 5am-11pm', desc: 'Available daily' },
        { title: 'Free Cancellation', desc: 'Up to 24h before without fees' },
        { title: 'Personal Support', desc: 'Direct contact person' },
      ],
      faqSubtitle: 'Frequently Asked',
      faqTitle: 'Questions about our prices?',
      faqDesc: 'Here you will find answers to the most common questions about our prices and payment options.',
      viewAllFaq: 'View all FAQs',
      faqs: [
        { q: 'What does parking cost for 2 weeks?', a: 'The price for 2 weeks shuttle parking is 72€, valet parking also 72€. You can get the exact price in the booking system.' },
        { q: 'Are the prices including all fees?', a: 'Yes, all prices shown are final prices including VAT and all fees.' },
        { q: 'When do I have to pay?', a: 'Payment is made when picking up the vehicle. No advance payment required.' },
        { q: 'Can I cancel for free?', a: 'Yes, you can cancel for free up to 24 hours before arrival.' },
      ],
      ctaTitle: 'Calculate your price now',
      ctaDesc: 'Enter your travel dates and get the exact price for your parking space immediately.',
      ctaBtn: 'To booking system',
    },
    // FAQ Page
    faq: {
      subtitle: 'FAQ',
      title1: 'Frequently Asked',
      title2: 'Questions',
      description: 'Here you will find answers to the most common questions about our parking service at Düsseldorf Airport.',
      categories: [
        {
          title: 'Booking & Reservation',
          questions: [
            { q: 'How do I book a parking space?', a: 'Booking is easy through our online booking system. Select your travel dates, the desired service (shuttle or valet) and complete the reservation. You will immediately receive a booking confirmation by email.' },
            { q: 'Can I change or cancel my booking?', a: 'Yes, you can cancel or change your booking free of charge up to 24 hours before arrival. Simply contact us by phone or email.' },
            { q: 'Do I have to pay in advance?', a: 'No, we do not require advance payment. You pay conveniently when picking up your vehicle.' },
            { q: 'Will I receive a booking confirmation?', a: 'Yes, immediately after your booking you will receive a confirmation by email with all important information about your parking space.' },
          ],
        },
        {
          title: 'Arrival & Process',
          questions: [
            { q: 'Where is the parking lot located?', a: 'Our parking lot is only 1 minute from Düsseldorf Airport. You will receive the exact address and directions with your booking confirmation.' },
            { q: 'How does the shuttle service work?', a: 'Drive directly to our parking lot, park your car and our shuttle will take you to the terminal within 1 minute. When you return, call us and we will pick you up directly at the terminal.' },
            { q: 'How does the valet service work?', a: 'With valet service, you drive directly to the departure terminal. There, our trained staff will take over your vehicle and park it safely for you. When you return, your car will be waiting for you at the terminal.' },
            { q: 'What happens if my flight is delayed?', a: 'No problem! We monitor flight times and adjust our service accordingly. You only pay for the actual parking time.' },
          ],
        },
        {
          title: 'Security & Service',
          questions: [
            { q: 'Is my vehicle safe?', a: 'Yes! Our premises are secured with barriers and gate systems. Key handover takes place at our reception area, where camera surveillance is also present. Trained staff supervise the parking lot.' },
            { q: 'Who drives my car with valet service?', a: 'Exclusively our trained and insured personnel. All employees are carefully selected and regularly trained.' },
            { q: 'Can I keep my car key?', a: 'As a rule, keys must be handed over so that we can move vehicles if necessary. However, upon request, it is possible to keep the key.' },
            { q: 'Do you also offer car care?', a: 'Yes, we have our own car wash and care facilities on site. You can book various car care packages – from simple exterior washing to complete interior and exterior cleaning.' },
          ],
        },
        {
          title: 'Prices & Payment',
          questions: [
            { q: 'How are prices calculated?', a: 'Prices depend on the chosen service (shuttle or valet). In the booking system you can see the exact price for your travel dates immediately.' },
            { q: 'Which payment methods are accepted?', a: 'We accept cash payment, debit card and all major credit cards. Payment is made when picking up the vehicle.' },
            { q: 'Are there hidden costs?', a: 'No, there are no hidden costs. The price shown in the booking system is the final price including VAT and all fees.' },
            { q: 'Will I receive an invoice?', a: 'Yes, you will automatically receive a proper invoice that you can also use for business trips.' },
          ],
        },
      ],
      contactTitle: 'Still have questions?',
      contactDesc: 'Our team is happy to help. Contact us – we\'re here for you!',
      emailBtn: 'Send email',
      contactFormLink: 'To contact form',
      bookCtaTitle: 'Convinced? Then book now!',
      bookCtaDesc: 'Secure your parking space at Düsseldorf Airport and start your vacation relaxed.',
      bookCtaBtn: 'Book parking now',
    },
    // Gallery
    gallery: {
      subtitle: 'Gallery',
      title1: 'Insights into our',
      title2: 'Service',
      description: 'See for yourself our premium parking service at Düsseldorf Airport.',
      ctaTitle: 'Convinced by our service?',
      ctaDesc: 'Book your parking space now and experience our premium service yourself.',
      ctaBtn: 'Book parking now',
      categories: {
        parkplatz: 'Parking',
        valet: 'Valet',
        shuttle: 'Shuttle',
        flughafen: 'Airport',
        autopflege: 'Car Care',
        fahrzeuge: 'Vehicles',
        terminal: 'Terminal',
        team: 'Team',
      },
    },
    // Contact
    contact: {
      subtitle: 'Contact',
      title1: 'We are here for',
      title2: 'you',
      description: 'Do you have questions or need support? Our team is happy to help.',
      phone: 'Phone',
      phoneAvailable: 'Available 5am-11pm',
      email: 'Email',
      emailResponse: 'Response within 24h',
      address: 'Address',
      addressNote: 'Right at the airport',
      hours: 'Opening Hours',
      hoursValue: 'Service 5am-11pm',
      hoursNote: 'Daily',
      formTitle: 'Write to us',
      formDesc: 'Fill out the form and we will get back to you as soon as possible.',
      name: 'Name',
      namePlaceholder: 'Your name',
      emailLabel: 'Email',
      emailPlaceholder: 'your@email.com',
      phonePlaceholder: '+49 123 456789',
      subject: 'Subject',
      subjectPlaceholder: 'Please select',
      subjects: {
        booking: 'Booking inquiry',
        price: 'Price inquiry',
        cancel: 'Cancellation',
        change: 'Booking change',
        feedback: 'Feedback',
        other: 'Other',
      },
      message: 'Message',
      messagePlaceholder: 'Your message to us...',
      submit: 'Send message',
      successTitle: 'Message sent!',
      successDesc: 'Thank you for your message. We will get back to you as soon as possible.',
      mapTitle: 'Find us here',
      mapDesc: 'Right at Düsseldorf Airport – only 1 minute to the terminal.',
      ctaTitle: 'Book directly – no detours',
      ctaDesc: 'Secure your parking space at Düsseldorf Airport now.',
      ctaBtn: 'To booking system',
    },
    // Impressum
    impressum: {
      title: 'Imprint',
      according: 'Information according to § 5 TMG',
      register: 'Commercial Register',
      court: 'Registration Court',
      representedBy: 'Represented by',
      contactTitle: 'Contact',
      vatId: 'VAT ID',
      vatIdDesc: 'VAT identification number according to § 27 a of the German VAT Act:',
      disputeTitle: 'Dispute Resolution',
      disputeText1: 'The European Commission provides a platform for online dispute resolution (OS):',
      disputeText2: 'You can find our email address in the imprint above.',
      disputeText3: 'We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.',
      liabilityContentTitle: 'Liability for Content',
      liabilityContentText1: 'As a service provider, we are responsible for our own content on these pages according to general laws.',
      liabilityContentText2: 'Obligations to remove or block the use of information according to general laws remain unaffected.',
      liabilityLinksTitle: 'Liability for Links',
      liabilityLinksText1: 'Our offer contains links to external websites of third parties, on whose contents we have no influence.',
      liabilityLinksText2: 'The linked pages were checked for possible legal violations at the time of linking.',
      copyrightTitle: 'Copyright',
      copyrightText1: 'The content and works created by the site operators on these pages are subject to German copyright law.',
      copyrightText2: 'Downloads and copies of this site are only permitted for private, non-commercial use.',
      toPrivacy: 'To Privacy Policy →',
    },
    // Privacy
    privacy: {
      title: 'Privacy Policy',
      lastUpdate: 'Last updated: January 2025',
      toImpressum: 'To Imprint →',
    },
  },
  nl: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'Over ons',
      services: 'Diensten',
      pricing: 'Prijzen',
      faq: 'FAQ',
      gallery: 'Galerij',
      contact: 'Contact',
      bookNow: 'Nu boeken',
    },
    // Hero
    hero: {
      badge: 'Luchthaven Düsseldorf',
      title1: 'Premium',
      title2: 'Valet Parking',
      title3: 'op de luchthaven',
      description: 'Stressvrij parkeren met onze exclusieve valet- en shuttleservice. Slechts 1 minuut van de terminal. Veilig, comfortabel en betrouwbaar.',
      cta: 'Nu boeken',
      services: 'Onze diensten',
    },
    // Benefits
    benefits: {
      terminal: 'Slechts 1 minuut naar terminal',
      secure: 'Beveiligd terrein met slagbomen & poorten',
      service: 'Service 5-23 uur',
      cancel: 'Gratis annulering',
    },
    // Stats
    stats: {
      experience: 'Jaar ervaring',
      parkingSpots: 'Parkeerplaatsen',
      satisfaction: 'Klanttevredenheid',
      parkedCars: 'Geparkeerde auto\'s',
    },
    // Services
    services: {
      title: 'Onze diensten',
      subtitle: 'Alles uit één hand',
      description: 'Kies de service die het beste bij uw behoeften past.',
      valet: {
        title: 'Valet Parking',
        description: 'Geef uw voertuig comfortabel af bij de terminal. Wij parkeren het veilig voor u.',
      },
      shuttle: {
        title: 'Shuttle Service',
        description: 'Gratis shuttletransfer tussen parkeerplaats en luchthaven terminal.',
      },
      carcare: {
        title: 'Autopflege',
        description: 'Professionele voertuigverzorging tijdens uw afwezigheid op aanvraag.',
      },
      learnMore: 'Meer informatie',
    },
    // Why Us
    whyUs: {
      subtitle: 'Waarom ParkingDUS?',
      title1: 'Uw vertrouwen is onze',
      title2: 'motivatie',
      description: 'Met meer dan 10 jaar ervaring op luchthaven Düsseldorf bieden wij u eersteklas parkeerservice. Ons team staat dagelijks van 5-23 uur voor u klaar om uw reis zo aangenaam mogelijk te maken.',
      features: [
        'Direct bij de luchthaven – slechts 1 minuut naar terminal',
        'Service 5-23 uur met persoonlijk contact',
        'Beveiligd terrein met slagbomen & poorten',
        'Eigen wasstraat & verzorgingshallen',
        'Gratis annulering tot 24 uur van tevoren',
      ],
      cta: 'Parkeerplaats reserveren',
      rating: 'Klantbeoordeling',
    },
    // CTA
    cta: {
      title1: 'Klaar voor een',
      title2: 'stressvrije',
      title3: 'reis?',
      description: 'Boek nu uw parkeerplaats en geniet van onze premium service op luchthaven Düsseldorf.',
      button: 'Nu parkeerplaats boeken',
    },
    // Footer
    footer: {
      description: 'Uw premium parkeerservice op luchthaven Düsseldorf. Stressvrij parkeren met valet- en shuttleservice.',
      service24: 'Service 5-23 uur',
      navigation: 'Navigatie',
      contact: 'Contact',
      legal: 'Juridisch',
      impressum: 'Impressum',
      privacy: 'Privacybeleid',
      copyright: 'ParkingDUS – MCD Group GmbH. Alle rechten voorbehouden.',
      madeWith: 'Made with',
      inDusseldorf: 'in Düsseldorf by',
    },
    // Cookie Banner
    cookie: {
      title: 'Cookie-instellingen',
      description: 'Wij gebruiken cookies om u de best mogelijke ervaring op onze website te bieden. Sommige cookies zijn noodzakelijk voor de functionaliteit van de website, terwijl andere ons helpen onze website te verbeteren.',
      learnMore: 'Meer informatie',
      acceptAll: 'Alles accepteren',
      rejectAll: 'Alleen noodzakelijke',
      settings: 'Instellingen',
      necessary: 'Noodzakelijke cookies',
      necessaryDesc: 'Deze cookies zijn vereist voor de basisfuncties van de website.',
      functional: 'Functionele cookies',
      functionalDesc: 'Deze cookies maken uitgebreide functies en personalisatie mogelijk.',
      analytics: 'Analytische cookies',
      analyticsDesc: 'Deze cookies helpen ons te begrijpen hoe bezoekers onze website gebruiken.',
      marketing: 'Marketing cookies',
      marketingDesc: 'Deze cookies worden gebruikt om reclame relevanter te maken.',
      saveSettings: 'Selectie opslaan',
      back: 'Terug',
    },
    // About Page
    about: {
      subtitle: 'Over ons',
      title1: 'Uw partner voor',
      title2: 'premium parkeren',
      description: 'ParkingDUS is uw betrouwbare partner voor stressvrij parkeren op luchthaven Düsseldorf. Met meer dan 10 jaar ervaring en een toegewijd team bieden wij u eersteklas service tegen eerlijke prijzen.',
      contactBtn: 'Neem contact op',
      yearsExp: 'Jaar ervaring',
      missionSubtitle: 'Onze missie',
      missionTitle: 'Stressvrij parkeren voor elke reiziger',
      missionDesc: 'Wij geloven dat de vakantie al moet beginnen bij de rit naar de luchthaven. Daarom hebben we het tot onze missie gemaakt om parkeren op de luchthaven zo eenvoudig en aangenaam mogelijk te maken.',
      values: {
        security: { title: 'Veiligheid', desc: 'Uw voertuig is bij ons in de beste handen. Getraind personeel garandeert maximale veiligheid.' },
        reliability: { title: 'Betrouwbaarheid', desc: 'Stiptheid is onze kracht. We zijn er voor u – dagelijks van 5-23 uur.' },
        service: { title: 'Klantenservice', desc: 'Uw tevredenheid is onze prioriteit. Ons team zorgt persoonlijk voor al uw zaken.' },
        quality: { title: 'Kwaliteit', desc: 'Wij stellen de hoogste normen op alle gebieden – van boeking tot voertuigoverdracht.' },
      },
      historySubtitle: 'Onze geschiedenis',
      historyTitle: 'De weg naar uw vertrouwen',
      timeline: [
        { year: '2014', title: 'Oprichting', desc: 'Gestart met slechts 50 parkeerplaatsen en een visie voor premium parkeerservice.' },
        { year: '2016', title: 'Uitbreiding', desc: 'Uitbreiding naar 200 parkeerplaatsen en introductie van valetservice.' },
        { year: '2019', title: 'Modernisering', desc: 'Implementatie van online boekingssysteem.' },
        { year: 'Vandaag', title: 'Toonaangevend', desc: '500 parkeerplaatsen en een van de toonaangevende aanbieders op luchthaven Düsseldorf.' },
      ],
      teamSubtitle: 'Ons team',
      teamTitle1: 'Mensen die',
      teamTitle2: 'zorgen',
      teamDesc: 'Achter ParkingDUS staat een toegewijd team van professionals die één ding gemeen hebben: passie voor uitstekende service. Elk teamlid is getraind en gemotiveerd om u de beste parkeerervaring te geven.',
      teamFeatures: [
        'Getraind en vriendelijk personeel',
        'Persoonlijk contactpersoon voor vragen',
        'Bereikbaar van 5-23 uur voor uw zaken',
        'Regelmatige training voor hoogste kwaliteit',
      ],
      ctaTitle: 'Overtuig uzelf',
      ctaDesc: 'Ervaar onze premium service en begin uw vakantie ontspannen.',
      ctaBtn: 'Nu parkeerplaats reserveren',
    },
    // Services Page
    servicesPage: {
      subtitle: 'Onze diensten',
      title1: 'Diensten die',
      title2: 'inspireren',
      description: 'Kies de service die het beste bij uw behoeften past. Van valet parking tot autoverzorging – wij hebben aan alles gedacht.',
      valet: {
        subtitle: 'De meest comfortabele manier',
        title: 'Valet Parking',
        description: 'Geef uw voertuig direct bij de terminal af aan ons getrainde personeel. Bij terugkeer staat uw auto al op u te wachten.',
        features: [
          'Voertuigoverdracht direct bij vertrek terminal',
          'Uw auto wacht op u bij terugkeer',
          'Geen parkeerplaats zoeken, geen shuttle nodig',
          'Getraind en verzekerd personeel',
          'Ideaal voor zakenreizigers',
        ],
      },
      shuttle: {
        subtitle: 'Het betaalbare alternatief',
        title: 'Shuttle Service',
        description: 'Parkeer uw voertuig veilig op onze parkeerplaats en gebruik onze gratis shuttleservice naar de terminal.',
        features: [
          'Gratis shuttletransfer naar terminal',
          'Slechts 1 minuut rijden naar luchthaven',
          'Flexibele ophaaltijden',
          'Geen wachten – individuele transfer',
          'Meest betaalbare optie voor langparkeren',
        ],
      },
      carcare: {
        subtitle: 'Extra service',
        title: 'Autoverzorging',
        description: 'Gebruik uw afwezigheid en laat uw voertuig professioneel reinigen. In onze eigen wasstraat en verzorgingshallen komt u terug naar een glanzende auto.',
        features: [
          'Eigen wasstraat op locatie',
          'Professionele verzorgingshallen',
          'Complete interieur- en exterieurreiniging',
          'Polijsten en lakbescherming',
          'Verschillende pakketten beschikbaar',
        ],
      },
      viewPrices: 'Prijzen bekijken',
      processSubtitle: 'Hoe het werkt',
      processTitle: 'Uw parkeerplaats in 4 stappen',
      process: [
        { step: '01', title: 'Online boeken', desc: 'Reserveer uw parkeerplaats gemakkelijk online in slechts enkele minuten.' },
        { step: '02', title: 'Aankomst', desc: 'Rijd direct naar onze locatie of naar de terminal (Valet).' },
        { step: '03', title: 'Ontspannen reizen', desc: 'Wij regelen alles – geniet van uw reis.' },
        { step: '04', title: 'Terugkeer', desc: 'Uw voertuig staat al op u te wachten – schoon en veilig.' },
      ],
      benefitsSubtitle: 'Uw voordelen',
      benefitsTitle: 'Waarom ParkingDUS?',
      benefitsList: [
        { title: 'Beste locatie', desc: 'Slechts 1 minuut van de terminal – dichterbij kan niet.' },
        { title: 'Beveiligd terrein', desc: 'Slagbomen & poorten voor maximale veiligheid.' },
        { title: 'Service 5-23 uur', desc: 'Dagelijks bereikbaar voor al uw vragen.' },
        { title: 'Gratis annulering', desc: 'Tot 24 uur van tevoren zonder kosten.' },
        { title: 'Eigen wasstraat', desc: 'Professionele autoverzorging in onze verzorgingshallen.' },
        { title: 'Extra diensten', desc: 'Autoverzorging en meer op aanvraag beschikbaar.' },
      ],
      ctaTitle: 'Klaar om te boeken?',
      ctaDesc: 'Reserveer nu uw parkeerplaats en begin uw vakantie ontspannen.',
      ctaBtn: 'Nu parkeerplaats boeken',
      callBtn: 'Bellen',
    },
    // Pricing Page
    pricing: {
      subtitle: 'Onze prijzen',
      title1: 'Transparant &',
      title2: 'eerlijk',
      description: 'Geen verborgen kosten, geen verrassingen. Kies de service die bij u past en krijg direct uw prijs.',
      shuttle: {
        name: 'Shuttle Parking',
        desc: 'De betaalbare optie met gratis transfer',
        price: 'vanaf 72',
        unit: '€',
        weekPrice: 'Vast tarief',
        features: [
          'Gratis shuttletransfer',
          'Slechts 1 minuut naar terminal',
          'Beveiligd terrein met slagbomen & poorten',
          'Service 5-23 uur',
          'Gratis annulering',
        ],
        cta: 'Shuttle boeken',
      },
      valet: {
        name: 'Valet Parking',
        desc: 'Maximaal comfort – direct bij de terminal',
        price: 'vanaf 72',
        unit: '€',
        weekPrice: 'Vast tarief',
        features: [
          'Voertuigoverdracht bij terminal',
          'Auto wacht bij terugkeer',
          'Geen parkeerplaats zoeken nodig',
          'Getraind personeel',
          'Premium-service',
          'Incl. gratis annulering',
        ],
        cta: 'Valet boeken',
        popular: 'POPULAIR',
      },
      carcare: {
        name: 'Autoverzorging',
        desc: 'Extra service voor een glanzende terugkeer',
        price: 'vanaf 49',
        unit: '€',
        weekPrice: 'Eenmalige prijs',
        features: [
          'Eigen wasstraat op locatie',
          'Professionele verzorgingshallen',
          'Interieurreiniging',
          'Boekbaar met alle diensten',
          'Verschillende pakketten',
        ],
        cta: 'Autoverzorging toevoegen',
      },
      priceNote: '* Prijzen kunnen variëren afhankelijk van seizoen en beschikbaarheid. De exacte prijs wordt getoond in het boekingssysteem.',
      includedSubtitle: 'Inbegrepen diensten',
      includedTitle: 'Inbegrepen bij elk tarief',
      included: [
        { title: 'Verzekeringsdekking', desc: 'Volledige dekking tijdens het gehele verblijf' },
        { title: 'Service 5-23 uur', desc: 'Dagelijks bereikbaar' },
        { title: 'Gratis annulering', desc: 'Tot 24 uur van tevoren zonder kosten' },
        { title: 'Persoonlijke ondersteuning', desc: 'Direct contactpersoon' },
      ],
      faqSubtitle: 'Veelgestelde vragen',
      faqTitle: 'Vragen over onze prijzen?',
      faqDesc: 'Hier vindt u antwoorden op de meest voorkomende vragen over onze prijzen en betalingsopties.',
      viewAllFaq: 'Alle FAQ\'s bekijken',
      faqs: [
        { q: 'Wat kost parkeren voor 2 weken?', a: 'De prijs voor 2 weken shuttle parking is 72€, valet parking ook 72€. U krijgt de exacte prijs in het boekingssysteem.' },
        { q: 'Zijn de prijzen inclusief alle kosten?', a: 'Ja, alle getoonde prijzen zijn eindprijzen inclusief BTW en alle kosten.' },
        { q: 'Wanneer moet ik betalen?', a: 'Betaling vindt plaats bij het ophalen van het voertuig. Geen vooruitbetaling vereist.' },
        { q: 'Kan ik gratis annuleren?', a: 'Ja, u kunt tot 24 uur voor aankomst gratis annuleren.' },
      ],
      ctaTitle: 'Bereken nu uw prijs',
      ctaDesc: 'Voer uw reisdata in en krijg direct de exacte prijs voor uw parkeerplaats.',
      ctaBtn: 'Naar boekingssysteem',
    },
    // FAQ Page
    faq: {
      subtitle: 'FAQ',
      title1: 'Veelgestelde',
      title2: 'vragen',
      description: 'Hier vindt u antwoorden op de meest voorkomende vragen over onze parkeerservice op luchthaven Düsseldorf.',
      categories: [
        {
          title: 'Boeking & Reservering',
          questions: [
            { q: 'Hoe boek ik een parkeerplaats?', a: 'Boeken is eenvoudig via ons online boekingssysteem. Selecteer uw reisdata, de gewenste service (shuttle of valet) en rond de reservering af. U ontvangt direct een boekingsbevestiging per e-mail.' },
            { q: 'Kan ik mijn boeking wijzigen of annuleren?', a: 'Ja, u kunt uw boeking tot 24 uur voor aankomst gratis annuleren of wijzigen. Neem gewoon contact met ons op via telefoon of e-mail.' },
            { q: 'Moet ik vooruit betalen?', a: 'Nee, wij vereisen geen vooruitbetaling. U betaalt gemakkelijk bij het ophalen van uw voertuig.' },
            { q: 'Ontvang ik een boekingsbevestiging?', a: 'Ja, direct na uw boeking ontvangt u een bevestiging per e-mail met alle belangrijke informatie over uw parkeerplaats.' },
          ],
        },
        {
          title: 'Aankomst & Proces',
          questions: [
            { q: 'Waar is de parkeerplaats?', a: 'Onze parkeerplaats bevindt zich op slechts 1 minuut van luchthaven Düsseldorf. U ontvangt het exacte adres en de routebeschrijving met uw boekingsbevestiging.' },
            { q: 'Hoe werkt de shuttleservice?', a: 'Rijd direct naar onze parkeerplaats, parkeer uw auto en onze shuttle brengt u binnen 1 minuut naar de terminal. Bij terugkeer belt u ons en wij halen u direct op bij de terminal.' },
            { q: 'Hoe werkt de valetservice?', a: 'Met valetservice rijdt u direct naar de vertrek terminal. Daar neemt ons getrainde personeel uw voertuig over en parkeert het veilig voor u. Bij terugkeer staat uw auto al bij de terminal op u te wachten.' },
            { q: 'Wat gebeurt er als mijn vlucht vertraging heeft?', a: 'Geen probleem! Wij monitoren vluchttijden en passen onze service daarop aan. U betaalt alleen voor de werkelijke parkeertijd.' },
          ],
        },
        {
          title: 'Veiligheid & Service',
          questions: [
            { q: 'Is mijn voertuig veilig?', a: 'Ja! Onze parkeerplaats wordt beheerd door getraind personeel. De sleuteloverdracht vindt plaats bij onze receptie, waar ook camerabewaking aanwezig is.' },
            { q: 'Wie rijdt mijn auto bij valetservice?', a: 'Uitsluitend ons getrainde en verzekerde personeel. Alle medewerkers worden zorgvuldig geselecteerd en regelmatig getraind.' },
            { q: 'Kan ik mijn autosleutel houden?', a: 'In de regel moeten sleutels worden afgegeven zodat wij voertuigen indien nodig kunnen verplaatsen. Op verzoek is het echter mogelijk om de sleutel te houden.' },
            { q: 'Bieden jullie ook autoverzorging aan?', a: 'Ja, u kunt op aanvraag verschillende autoverzorgingspakketten boeken. Van eenvoudige buitenwas tot complete interieur- en exterieurreiniging, alles is mogelijk.' },
          ],
        },
        {
          title: 'Prijzen & Betaling',
          questions: [
            { q: 'Hoe worden prijzen berekend?', a: 'Prijzen zijn afhankelijk van de gekozen service (shuttle of valet). In het boekingssysteem ziet u direct de exacte prijs voor uw reisdata.' },
            { q: 'Welke betaalmethoden worden geaccepteerd?', a: 'Wij accepteren contante betaling, pinpas en alle gangbare creditcards. Betaling vindt plaats bij het ophalen van het voertuig.' },
            { q: 'Zijn er verborgen kosten?', a: 'Nee, er zijn geen verborgen kosten. De prijs die in het boekingssysteem wordt getoond, is de eindprijs inclusief BTW en alle kosten.' },
            { q: 'Ontvang ik een factuur?', a: 'Ja, u ontvangt automatisch een correcte factuur die u ook voor zakenreizen kunt gebruiken.' },
          ],
        },
      ],
      contactTitle: 'Nog vragen?',
      contactDesc: 'Ons team helpt u graag. Neem contact met ons op – wij staan voor u klaar!',
      emailBtn: 'E-mail sturen',
      contactFormLink: 'Naar contactformulier',
      bookCtaTitle: 'Overtuigd? Boek nu!',
      bookCtaDesc: 'Reserveer uw parkeerplaats op luchthaven Düsseldorf en begin uw vakantie ontspannen.',
      bookCtaBtn: 'Nu parkeerplaats boeken',
    },
    // Gallery
    gallery: {
      subtitle: 'Galerij',
      title1: 'Inzichten in onze',
      title2: 'service',
      description: 'Overtuig uzelf van onze premium parkeerservice op luchthaven Düsseldorf.',
      ctaTitle: 'Overtuigd van onze service?',
      ctaDesc: 'Boek nu uw parkeerplaats en ervaar onze premium service zelf.',
      ctaBtn: 'Nu parkeerplaats boeken',
      categories: {
        parkplatz: 'Parkeren',
        valet: 'Valet',
        shuttle: 'Shuttle',
        flughafen: 'Luchthaven',
        autopflege: 'Autoverzorging',
        fahrzeuge: 'Voertuigen',
        terminal: 'Terminal',
        team: 'Team',
      },
    },
    // Contact
    contact: {
      subtitle: 'Contact',
      title1: 'Wij zijn er voor',
      title2: 'u',
      description: 'Heeft u vragen of heeft u ondersteuning nodig? Ons team helpt u graag.',
      phone: 'Telefoon',
      phoneAvailable: 'Bereikbaar 5-23 uur',
      email: 'E-mail',
      emailResponse: 'Antwoord binnen 24u',
      address: 'Adres',
      addressNote: 'Direct bij de luchthaven',
      hours: 'Servicetijden',
      hoursValue: '5-23 uur',
      hoursNote: 'Dagelijks',
      formTitle: 'Schrijf ons',
      formDesc: 'Vul het formulier in en wij nemen zo snel mogelijk contact met u op.',
      name: 'Naam',
      namePlaceholder: 'Uw naam',
      emailLabel: 'E-mail',
      emailPlaceholder: 'uw@email.nl',
      phonePlaceholder: '+31 123 456789',
      subject: 'Onderwerp',
      subjectPlaceholder: 'Selecteer a.u.b.',
      subjects: {
        booking: 'Boekingsaanvraag',
        price: 'Prijsaanvraag',
        cancel: 'Annulering',
        change: 'Boekingswijziging',
        feedback: 'Feedback',
        other: 'Overig',
      },
      message: 'Bericht',
      messagePlaceholder: 'Uw bericht aan ons...',
      submit: 'Bericht versturen',
      successTitle: 'Bericht verzonden!',
      successDesc: 'Bedankt voor uw bericht. Wij nemen zo snel mogelijk contact met u op.',
      mapTitle: 'Vind ons hier',
      mapDesc: 'Direct bij luchthaven Düsseldorf – slechts 1 minuut naar de terminal.',
      ctaTitle: 'Direct boeken – geen omwegen',
      ctaDesc: 'Reserveer nu uw parkeerplaats op luchthaven Düsseldorf.',
      ctaBtn: 'Naar boekingssysteem',
    },
    // Impressum
    impressum: {
      title: 'Impressum',
      according: 'Informatie volgens § 5 TMG',
      register: 'Handelsregister',
      court: 'Registratie rechtbank',
      representedBy: 'Vertegenwoordigd door',
      contactTitle: 'Contact',
      vatId: 'BTW-ID',
      vatIdDesc: 'BTW-identificatienummer volgens § 27 a van de Duitse BTW-wet:',
      disputeTitle: 'Geschillenbeslechting',
      disputeText1: 'De Europese Commissie biedt een platform voor online geschillenbeslechting (OS):',
      disputeText2: 'U vindt ons e-mailadres in het impressum hierboven.',
      disputeText3: 'Wij zijn niet bereid of verplicht om deel te nemen aan geschillenbeslechtingsprocedures voor een consumentenarbitragecommissie.',
      liabilityContentTitle: 'Aansprakelijkheid voor inhoud',
      liabilityContentText1: 'Als dienstverlener zijn wij verantwoordelijk voor onze eigen inhoud op deze pagina\'s volgens de algemene wetten.',
      liabilityContentText2: 'Verplichtingen om het gebruik van informatie te verwijderen of te blokkeren volgens de algemene wetten blijven onaangetast.',
      liabilityLinksTitle: 'Aansprakelijkheid voor links',
      liabilityLinksText1: 'Ons aanbod bevat links naar externe websites van derden, op de inhoud waarvan wij geen invloed hebben.',
      liabilityLinksText2: 'De gelinkte pagina\'s werden gecontroleerd op mogelijke juridische overtredingen op het moment van linken.',
      copyrightTitle: 'Auteursrecht',
      copyrightText1: 'De inhoud en werken die door de site-exploitanten op deze pagina\'s zijn gemaakt, zijn onderworpen aan het Duitse auteursrecht.',
      copyrightText2: 'Downloads en kopieën van deze site zijn alleen toegestaan voor privé, niet-commercieel gebruik.',
      toPrivacy: 'Naar privacybeleid →',
    },
    // Privacy
    privacy: {
      title: 'Privacybeleid',
      lastUpdate: 'Laatst bijgewerkt: januari 2025',
      toImpressum: 'Naar impressum →',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('language');
      if (saved && ['de', 'en', 'nl'].includes(saved)) {
        return saved;
      }
    }
    return 'de';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = translations[language];

  const changeLanguage = (lang) => {
    if (['de', 'en', 'nl'].includes(lang)) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
